import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { downloadBlob } from "../../../utils/downloadBlob";
import type { PbjExclusionImportPreview, PbjExclusionImportRow, PbjExclusionImportRowStatus } from "../../../utils/models";
import { useImportPbjExclusions } from "../hooks/usePbjAudit";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
};

type Step = "upload" | "preview";

// Status → theme chip color (MUI palette tokens, no hardcoded hex). The id-keyed import reuses the
// override enum: new (will exclude) = success, removed (will clear an exclusion) = error, unchanged = neutral.
const STATUS_COLOR: Record<PbjExclusionImportRowStatus, "success" | "info" | "error" | "default"> = {
	new: "success",
	removed: "error",
	unchanged: "default",
	updated: "info",
};

// Blank sample file the analyst fills in. The importer keys on the RAW synced pbj_id (ID column); a
// non-blank Reason excludes the employee, a blank Reason clears an existing exclusion. The example ids
// are obviously-fake placeholders meant to be overwritten; the second row's blank reason demonstrates a clear.
const EXCLUSION_TEMPLATE_CSV = ["ID,Reason", "100001,Non-nursing role", "100002,"].join("\n");
const EXCLUSION_TEMPLATE_FILENAME = "pbj_exclude_from_pbj_template.csv";
const CSV_MIME = "text/csv;charset=utf-8;";

const SummaryTile: FC<{ label: string; value: number | string }> = ({ label, value }) => (
	<Box sx={{ flex: "1 1 90px", p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5, textAlign: "center" }}>
		<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
			{value}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
);

/**
 * PBJ Audit → "Excluded from PBJ" → Bulk import: upload an ID + Reason list, keyed by the RAW synced
 * pbj_id (no fuzzy name matching). A non-blank reason excludes the employee; a blank reason clears an
 * existing exclusion. Preview the diff (current → new state) then commit. Two steps in one dialog — the
 * file is dry-run on "Preview" and re-sent on "Confirm". A block-severity warning (unknown / duplicate
 * pbj_id) sets `blocked` and disables the commit. Modeled on ManageOverridesDialog's upload flow.
 */
export const ImportExclusionsDialog: FC<Props> = ({ open, onClose, departmentId }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const importMutation = useImportPbjExclusions();

	const [step, setStep] = useState<Step>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<PbjExclusionImportPreview | null>(null);

	const isRunning = importMutation.isPending;

	const reset = useCallback(() => {
		setStep("upload");
		setFile(null);
		setPreview(null);
	}, []);

	const handleClose = useCallback(() => {
		if (isRunning) return;
		reset();
		onClose();
	}, [isRunning, reset, onClose]);

	useEffect(() => {
		if (open) track("pbj_exclusion_import_opened", { department_id: departmentId });
	}, [open, track, departmentId]);

	// Client-side download of the blank exclusion template (no round-trip needed — static columns).
	const handleDownloadTemplate = useCallback(() => {
		const bytes = new TextEncoder().encode(EXCLUSION_TEMPLATE_CSV);
		downloadBlob(bytes.buffer as ArrayBuffer, EXCLUSION_TEMPLATE_FILENAME, CSV_MIME);
		track("pbj_exclusion_import_template_downloaded", { department_id: departmentId });
	}, [track, departmentId]);

	const handlePreview = useCallback(async () => {
		if (!file || isRunning) return;
		try {
			const result = await importMutation.mutateAsync({ file, dryRun: true });
			setPreview(result);
			setStep("preview");
		} catch (error) {
			showSnackbar(t("pbj_model.exclusion_import.failed"), "error");
			trackError("pbj_exclusion_import_error", { department_id: departmentId, phase: "preview", error_message: (error as Error)?.message });
		}
	}, [file, isRunning, importMutation, showSnackbar, t, trackError, departmentId]);

	const handleConfirm = useCallback(async () => {
		if (!file || isRunning || preview?.blocked) return;
		try {
			const result = await importMutation.mutateAsync({ file, dryRun: false });
			setPreview(result);
			if (result.committed) {
				track("pbj_exclusion_import_committed", {
					department_id: departmentId,
					excluded: result.summary.excluded,
					cleared: result.summary.cleared,
				});
				showSnackbar(t("pbj_model.exclusion_import.success", { count: result.summary.excluded }), "success");
				handleClose();
			} else {
				showSnackbar(t("pbj_model.exclusion_import.failed"), "error");
			}
		} catch (error) {
			showSnackbar(t("pbj_model.exclusion_import.failed"), "error");
			trackError("pbj_exclusion_import_error", { department_id: departmentId, phase: "confirm", error_message: (error as Error)?.message });
		}
	}, [file, isRunning, preview, importMutation, track, showSnackbar, t, trackError, departmentId, handleClose]);

	const columns: GridColDef<PbjExclusionImportRow>[] = useMemo(
		() => [
			{
				field: "status",
				headerName: t("pbj_model.exclusion_import.cols.status"),
				minWidth: 120,
				renderCell: ({ value }) => (
					<Chip size="small" color={STATUS_COLOR[value as PbjExclusionImportRowStatus]} label={t(`pbj_model.exclusion_import.status.${value}`)} />
				),
			},
			{ field: "pbjEmployeeId", headerName: t("pbj_model.exclusion_import.cols.id"), minWidth: 120, flex: 1 },
			{ field: "name", headerName: t("pbj_model.exclusion_import.cols.name"), minWidth: 160, flex: 1 },
			{
				field: "currentlyExcluded",
				headerName: t("pbj_model.exclusion_import.cols.current"),
				minWidth: 130,
				valueGetter: (_v, row) =>
					row.currentlyExcluded ? t("pbj_model.exclusion_import.current.excluded") : t("pbj_model.exclusion_import.current.not_excluded"),
			},
			{
				field: "newReason",
				headerName: t("pbj_model.exclusion_import.cols.reason"),
				minWidth: 180,
				flex: 1,
				valueGetter: (v: string | null) => v ?? "—",
			},
		],
		[t],
	);

	const blockers = preview?.warnings.filter((w) => w.severity === "block") ?? [];
	const softWarnings = preview?.warnings.filter((w) => w.severity === "warn") ?? [];

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth data-testid="pbj-exclusion-import-dialog">
			<DialogTitle sx={{ fontWeight: 700 }}>{t("pbj_model.exclusion_import.title")}</DialogTitle>
			<DialogContent>
				{step === "upload" ? (
					<Stack spacing={2.5} sx={{ pt: 1 }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
							<Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
								{t("pbj_model.exclusion_import.description")}
							</Typography>
							<SystemButton
								buttonVariant="borderless"
								noFullWidthMobile
								leadingIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
								onClick={handleDownloadTemplate}
								data-testid="pbj-exclusion-import-download-template"
							>
								{t("pbj_model.exclusion_import.download_template")}
							</SystemButton>
						</Box>
						<Alert severity="info">{t("pbj_model.exclusion_import.columns_hint")}</Alert>
						<FileDropZone
							accept=".xlsx,.xls,.csv"
							hint={t("pbj_model.exclusion_import.file_hint")}
							onFiles={(files) => setFile(files[0] ?? null)}
							dataTestId="pbj-exclusion-import-dropzone"
						/>
						{file && (
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{file.name}
							</Typography>
						)}
					</Stack>
				) : (
					preview && (
						<Stack spacing={2} sx={{ pt: 1 }}>
							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
								<SummaryTile label={t("pbj_model.exclusion_import.summary.file_rows")} value={preview.summary.fileRows} />
								<SummaryTile label={t("pbj_model.exclusion_import.summary.excluded")} value={preview.summary.excluded} />
								<SummaryTile label={t("pbj_model.exclusion_import.summary.cleared")} value={preview.summary.cleared} />
								<SummaryTile label={t("pbj_model.exclusion_import.summary.unchanged")} value={preview.summary.unchanged} />
								<SummaryTile label={t("pbj_model.exclusion_import.summary.blocked")} value={preview.summary.blocked} />
							</Box>

							{blockers.map((w) => (
								<Alert key={`${w.code}-${w.message}`} severity="error">
									{w.message}
								</Alert>
							))}
							{softWarnings.map((w) => (
								<Alert key={`${w.code}-${w.message}`} severity="warning">
									{w.message}
								</Alert>
							))}

							<Box sx={{ height: 420 }}>
								<SystemDataGrid<PbjExclusionImportRow>
									rows={preview.rows}
									columns={columns}
									getRowId={(r) => r.pbjEmployeeId}
									searchableColumns={["pbjEmployeeId", "name"]}
									defaultPageSize={25}
									containedHeight
									dataTestId="pbj-exclusion-import-preview-table"
								/>
							</Box>
						</Stack>
					)
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<SystemButton buttonVariant="borderless" noFullWidthMobile onClick={handleClose} disabled={isRunning} data-testid="pbj-exclusion-import-cancel">
					{t("cancel")}
				</SystemButton>
				{step === "preview" ? (
					<>
						<SystemButton
							buttonVariant="borderless"
							noFullWidthMobile
							onClick={() => setStep("upload")}
							disabled={isRunning}
							data-testid="pbj-exclusion-import-back"
						>
							{t("pbj_model.exclusion_import.back")}
						</SystemButton>
						<SystemButton
							buttonVariant="primary"
							noFullWidthMobile
							loading={isRunning}
							disabled={preview?.blocked ?? true}
							tooltip={preview?.blocked ? t("pbj_model.exclusion_import.blocked_tooltip") : undefined}
							onClick={() => void handleConfirm()}
							data-testid="pbj-exclusion-import-confirm"
						>
							{t("pbj_model.exclusion_import.confirm")}
						</SystemButton>
					</>
				) : (
					<SystemButton
						buttonVariant="primary"
						noFullWidthMobile
						loading={isRunning}
						disabled={!file}
						onClick={() => void handlePreview()}
						data-testid="pbj-exclusion-import-preview"
					>
						{t("pbj_model.exclusion_import.preview")}
					</SystemButton>
				)}
			</DialogActions>
		</Dialog>
	);
};
